import React, { useEffect, useState, useRef } from 'react';
import { useReducedMotion, useInView } from 'framer-motion';
import { motion } from '../lib/motionShim';
import PhoneFrame from './PhoneFrame';

/**
 * Two phones sharing a front-and-back stage, trading places on a spring
 * every few seconds — same choreography as the coach duo in 07 · GUIDE.
 * The slots preserve each section's original staging: front centered and
 * full size, back offset to one side, scaled down, tilted and dimmed.
 */

const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const PhoneSwap = ({ front, back, width = 296, side = 'right', interval = 4600 }) => {
  const reduce = useReducedMotion();
  const isMobile = useIsMobile();
  const containerRef = useRef(null);
  // Swap only while on screen — no point springing phones nobody can see
  const isOnScreen = useInView(containerRef, { margin: '100px' });
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    if (reduce || !isOnScreen) return undefined;
    const id = setInterval(() => setFlipped((f) => !f), interval);
    return () => clearInterval(id);
  }, [reduce, interval, isOnScreen]);

  const dir = side === 'right' ? 1 : -1;
  const frontSlot = { x: 0, y: 0, scale: 1, rotateY: 0, opacity: 1 };
  const backSlot = isMobile
    ? { x: dir * 30, y: 18, scale: 0.9, rotateY: -10 * dir, opacity: 0.55 }
    : {
        x: side === 'right' ? 132 : -96,
        y: 40,
        scale: 0.88,
        rotateY: -14 * dir,
        opacity: 0.75,
      };
  const spring = { type: 'spring', stiffness: 80, damping: 17 };

  const phones = [
    { ...front, isFront: !flipped },
    { ...back, isFront: flipped },
  ];

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ width, height: Math.round(width * 2.17) + 44, perspective: 1500 }}
    >
      {phones.map((p) => (
        <motion.div
          key={p.src}
          className="absolute top-0 left-0"
          style={{ zIndex: p.isFront ? 20 : 10, transformOrigin: 'center center' }}
          initial={false}
          animate={reduce ? (p.isFront ? frontSlot : backSlot) : (p.isFront ? frontSlot : backSlot)}
          transition={spring}
        >
          <PhoneFrame
            src={p.src}
            alt={p.alt}
            width={width}
            glowColor={p.glowColor || 'rgba(147,51,234,0.25)'}
            glow={p.isFront}
          />
        </motion.div>
      ))}
    </div>
  );
};

export default PhoneSwap;
